/**
 * Accounts Receivable & Dunning ("Debitoren-Cockpit") — API client.
 *
 * Thin typed wrapper over fetchApi (which unwraps the {success,data} envelope).
 * The open-items list needs the pagination block too, so it uses a raw fetch.
 */

import { fetchApi } from './api';
import type {
    ArStats, ArFilters, ArOpenItemsPage, ArOpenItemsFilter, ArAction, ArActionType,
    ArComment, ArEntityType, ArTemplate, ArTemplateAudience, ArTemplateLanguage,
    ArConfig, ArConfigResponse, ArEmailHistoryEntry, ArScope,
    ArSendEmailBody, ArSendEmailResult, ArDigestResult, ArCustomerRiskResponse, ArCustomerRiskInput,
} from '../types/arDunning';

const BASE_URL = '/api/v1';

/**
 * Fetch a binary endpoint (PDF / xlsx) and trigger a browser download. Surfaces
 * server errors as thrown Errors (so callers can toast) instead of navigating to an
 * error page, and honours the 401 login redirect like the JSON client.
 */
async function downloadFile(url: string, fallbackName: string): Promise<void> {
    const res = await fetch(`${BASE_URL}${url}`);
    if (res.status === 401) {
        const { redirectToLogin } = await import('../utils/auth-redirect');
        redirectToLogin();
        throw new Error('Session expired');
    }
    if (!res.ok) {
        // Error responses are JSON envelopes; pull the message out if we can.
        let message = `Download failed (status ${res.status})`;
        try {
            const body = await res.json() as { error?: { message?: string } };
            if (body.error?.message) message = body.error.message;
        } catch { /* non-JSON error page */ }
        throw new Error(message);
    }
    const disposition = res.headers.get('content-disposition') || '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const filename = match?.[1] || fallbackName;
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
}

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
    }
    const s = sp.toString();
    return s ? `?${s}` : '';
}

/** Raw fetch that also returns the pagination block (fetchApi drops it). */
async function fetchPaginated(url: string): Promise<ArOpenItemsPage> {
    const res = await fetch(`${BASE_URL}${url}`, { headers: { 'Content-Type': 'application/json' } });
    if (res.status === 401) {
        const { redirectToLogin } = await import('../utils/auth-redirect');
        redirectToLogin();
        throw new Error('Session expired');
    }
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
        const text = await res.text().catch(() => '');
        const snippet = text.replace(/<[^>]+>/g, ' ').trim().slice(0, 200);
        throw new Error(`Server error ${res.status}.${snippet ? ` ${snippet}` : ''}`);
    }
    const body = await res.json() as {
        success: boolean; data?: { items: ArOpenItemsPage['items']; scope: ArScope; openAmountEUR: number };
        pagination?: ArOpenItemsPage['pagination']; error?: { message: string };
    };
    if (!res.ok || !body.success || !body.data) {
        throw new Error(body.error?.message || `API error: ${res.status}`);
    }
    return {
        items: body.data.items,
        scope: body.data.scope,
        openAmountEUR: body.data.openAmountEUR,
        pagination: body.pagination ?? { page: 1, limit: body.data.items.length, total: body.data.items.length, totalPages: 1, hasNext: false, hasPrev: false },
    };
}

export const arDunningApi = {
    // ---- Open items ---------------------------------------------------------
    listOpenItems(filter: ArOpenItemsFilter = {}): Promise<ArOpenItemsPage> {
        return fetchPaginated(`/ar-dunning/open-items${qs({
            companyCode: filter.companyCode,
            businessUnit: filter.businessUnit,
            salesManagerId: filter.salesManagerId,
            customerId: filter.customerId,
            agingBucket: filter.agingBucket,
            itemCategory: filter.itemCategory,
            overdueOnly: filter.overdueOnly ? '1' : undefined,
            disputed: filter.disputed,
            hasPromise: filter.hasPromise,
            q: filter.q,
            sort: filter.sort,
            page: filter.page,
            limit: filter.limit,
        })}`);
    },

    /** Load the full scoped set by looping pages (used by Aging + list views). */
    async listAllOpenItems(filter: Omit<ArOpenItemsFilter, 'page' | 'limit'> = {}, opts: { maxPages?: number; pageSize?: number } = {}): Promise<{ items: ArOpenItemsPage['items']; scope: ArScope; total: number; truncated: boolean }> {
        // Load the full scoped set in ONE request where possible: the server caps limit at
        // 5000 and the firm-wide open-item set is ~2.7k rows, so a single page normally covers
        // everything. This avoids the old 6× sequential loop that re-materialised the heavy
        // view on every page. maxPages stays as a safety net if the data ever exceeds one page.
        const pageSize = opts.pageSize ?? 5000;
        const maxPages = opts.maxPages ?? 12;
        let page = 1;
        const items: ArOpenItemsPage['items'] = [];
        let scope: ArScope = { mode: 'all', isCentral: true };
        let total = 0;
        let truncated = false;
        for (; page <= maxPages; page++) {
            const res = await this.listOpenItems({ ...filter, page, limit: pageSize });
            scope = res.scope;
            total = res.pagination.total;
            items.push(...res.items);
            if (!res.pagination.hasNext) break;
            if (page === maxPages && res.pagination.hasNext) truncated = true;
        }
        return { items, scope, total, truncated };
    },

    // ---- Stats & filters ----------------------------------------------------
    getStats(companyCode?: string, businessUnit?: string): Promise<ArStats> {
        return fetchApi<ArStats>(`/ar-dunning/stats${qs({ companyCode, businessUnit })}`);
    },
    getFilters(): Promise<ArFilters> {
        return fetchApi<ArFilters>(`/ar-dunning/filters`);
    },

    // ---- Actions (promise / follow-up / dispute / reminder) -----------------
    listActionsForEntity(entityType: ArEntityType, entityRef: string): Promise<{ actions: ArAction[] }> {
        return fetchApi<{ actions: ArAction[] }>(`/ar-dunning/actions${qs({ entityType, entityRef })}`);
    },
    listActionsForCustomer(customerId: string): Promise<{ actions: ArAction[] }> {
        return fetchApi<{ actions: ArAction[] }>(`/ar-dunning/actions${qs({ customerId })}`);
    },
    listMyActions(status?: string): Promise<{ actions: ArAction[] }> {
        return fetchApi<{ actions: ArAction[] }>(`/ar-dunning/actions${qs({ mine: '1', status })}`);
    },
    createAction(body: {
        entityType: ArEntityType; entityRef: string; actionType: ArActionType;
        dunningLevel?: number; promiseDate?: string; promiseAmount?: number | null;
        dueAt?: string; assignee?: string; notes?: string; emailLogId?: number | null;
    }): Promise<{ Id: number; CreatedAt: string; CompanyCode: string; CustomerID: string | null; Status: string }> {
        return fetchApi(`/ar-dunning/actions`, { method: 'POST', body: JSON.stringify(body) });
    },
    updateAction(id: number, body: {
        status?: string; notes?: string; dueAt?: string | null; promiseDate?: string | null; promiseAmount?: number | null;
    }): Promise<{ Id: number; updated: boolean }> {
        return fetchApi(`/ar-dunning/actions/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
    },

    // ---- Comments (invoice- or customer-grain) ------------------------------
    listComments(entityType: ArEntityType, entityRef: string): Promise<{ comments: ArComment[] }> {
        return fetchApi<{ comments: ArComment[] }>(`/ar-dunning/comments${qs({ entityType, entityRef })}`);
    },
    createComment(entityType: ArEntityType, entityRef: string, commentText: string): Promise<ArComment> {
        return fetchApi<ArComment>(`/ar-dunning/comments`, { method: 'POST', body: JSON.stringify({ entityType, entityRef, commentText }) });
    },
    updateComment(id: number, commentText: string): Promise<{ Id: number; CommentText: string }> {
        return fetchApi(`/ar-dunning/comments/${id}`, { method: 'PUT', body: JSON.stringify({ commentText }) });
    },
    async deleteComment(id: number): Promise<void> {
        await fetchApi(`/ar-dunning/comments/${id}`, { method: 'DELETE' });
    },

    // ---- Dunning e-mail send + history -------------------------------------
    sendEmail(body: ArSendEmailBody): Promise<ArSendEmailResult> {
        return fetchApi<ArSendEmailResult>(`/ar-dunning/emails/send`, { method: 'POST', body: JSON.stringify(body) });
    },
    listEmailHistory(opts: { entityRef?: string; limit?: number } = {}): Promise<{ emails: ArEmailHistoryEntry[]; scope: ArScope }> {
        return fetchApi<{ emails: ArEmailHistoryEntry[]; scope: ArScope }>(`/ar-dunning/emails${qs({ entityRef: opts.entityRef, limit: opts.limit })}`);
    },

    // ---- Customer contact overlay (manual dunning e-mail / language) --------
    upsertCustomerContact(body: { companyCode: string; customerId: string; email?: string | null; languageCode?: string | null }): Promise<{ Id: number | null; customerId: string; email: string | null; languageCode: string | null }> {
        return fetchApi(`/ar-dunning/customer-contact`, { method: 'PUT', body: JSON.stringify(body) });
    },

    // ---- Templates ----------------------------------------------------------
    listTemplates(opts: { level?: number; audience?: string; language?: string } = {}): Promise<{ templates: ArTemplate[] }> {
        return fetchApi<{ templates: ArTemplate[] }>(`/ar-dunning/templates${qs({ level: opts.level, audience: opts.audience, language: opts.language })}`);
    },
    upsertTemplate(body: { dunningLevel: number; audience: ArTemplateAudience; languageCode: ArTemplateLanguage; subject: string; bodyHtml: string }): Promise<{ Id: number | null }> {
        return fetchApi(`/ar-dunning/templates`, { method: 'PUT', body: JSON.stringify(body) });
    },

    // ---- Config -------------------------------------------------------------
    getConfig(): Promise<ArConfigResponse> {
        return fetchApi<ArConfigResponse>(`/ar-dunning/config`);
    },
    updateConfig(body: Partial<ArConfig>): Promise<{ config: ArConfig }> {
        return fetchApi(`/ar-dunning/config`, { method: 'PUT', body: JSON.stringify(body) });
    },

    // ---- Central controllers / digest recipients ----------------------------
    upsertRecipient(body: { userEmail: string; isCentral: boolean; digestOptIn: boolean }): Promise<{ Id: number | null; userEmail: string; isCentral: boolean; digestOptIn: boolean }> {
        return fetchApi(`/ar-dunning/recipients`, { method: 'PUT', body: JSON.stringify(body) });
    },
    async deleteRecipient(id: number): Promise<void> {
        await fetchApi(`/ar-dunning/recipients/${id}`, { method: 'DELETE' });
    },

    // ---- Payment behaviour & risk (Phase 7) ---------------------------------
    /** Scores the given customers (open aggregates computed client-side from loaded items). */
    getCustomerRisk(customers: ArCustomerRiskInput[], companyCode?: string): Promise<ArCustomerRiskResponse> {
        return fetchApi<ArCustomerRiskResponse>(`/ar-dunning/customer-risk`, {
            method: 'POST',
            body: JSON.stringify({ customers, companyCode }),
        });
    },

    // ---- Downloads: statement PDF + open-items Excel ------------------------
    downloadStatement(customerId: string, companyCode?: string, lang?: string): Promise<void> {
        return downloadFile(
            `/ar-dunning/statement-pdf${qs({ customerId, companyCode, lang })}`,
            `Statement_${customerId}.pdf`,
        );
    },
    downloadOpenItemsExport(filter: { companyCode?: string; salesManagerId?: string; agingBucket?: string; overdueOnly?: boolean; q?: string } = {}): Promise<void> {
        return downloadFile(
            `/ar-dunning/export${qs({
                companyCode: filter.companyCode,
                salesManagerId: filter.salesManagerId,
                agingBucket: filter.agingBucket,
                overdueOnly: filter.overdueOnly ? '1' : undefined,
                q: filter.q,
            })}`,
            `AR_OpenItems.xlsx`,
        );
    },

    // ---- Digest (Phase 6, admin) --------------------------------------------
    /** Dry-run: compute today's digest content (firm-wide + per-seller counts). No mail sent. */
    previewDigest(): Promise<ArDigestResult> {
        return fetchApi<ArDigestResult>(`/ar-dunning/digest/preview`);
    },
    /** Send ONE firm-wide test digest to roman.dill@dolder.com (recipient override). */
    sendTestDigest(): Promise<ArDigestResult> {
        return fetchApi<ArDigestResult>(`/ar-dunning/digest/test`, { method: 'POST' });
    },
};
